import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { delay, filter, Observable, Subject, takeUntil } from 'rxjs';
import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { NotificationEvent } from '../../interface/notifications-event.interface';

import {
    AlertError,
    AlertSuccess,
    GeneratedReport,
    GenerateInstanceReport,
    GenerateReportError,
    GetInstanceReports,
    PollForEvents,
    PollingTerminated,
} from '../../store/actions';
import { getInstanceReportsMap$, getNotifications$, getReportsLoadingStatus$ } from '../../store/selectors';

@Component({
    selector: 'app-reports-dialog',
    templateUrl: './reports-dialog.component.html',
    styleUrls: ['./reports-dialog.component.scss'],
    standalone: false,
})
export class ReportsDialogComponent implements OnDestroy, OnInit {
    @Input() public record: any;
    @Input() public serviceId: string;

    private unSubscribe = new Subject<void>();
    public reports: any = [];
    public isCancelledProcess = false;
    public startedReportGeneration = false;
    public loadingReports = false;
    public triggeredTransformations: string[] = [];
    public reportCutoffTimestamp: number;
    public reportsLoading: Observable<boolean>;

    constructor(
        private actions$: Actions,
        private store$: Store,
        private dialogRef: MatDialogRef<ReportsDialogComponent>,
        private _api: ApiService,
        private _commonUtils: CommonUtilsService
    ) {}

    private getReports = (forceFetch?: boolean) => {
        this.store$.dispatch(GetInstanceReports({ instanceId: this.record?.recordId, forceFetch, noAlerts: false }));
    };

    public generateReport = chain => {
        this.startedReportGeneration = true;
        this.hideGenerateButton(chain);
        this.store$.dispatch(GenerateInstanceReport({ instanceId: this.record?.recordId, chainName: chain?.name }));
    };

    public hideGenerateButton(chain) {
        if (!chain?.transformations[0]?.reports[0]?.reportsList[0]) {
            return;
        }
        chain.transformations[0].reports[0].reportsList[0].hideRegenerate = true;
        this.reports = [...this.reports, chain];
    }

    public printReport = chain => {
        this._api.reports.printFile(chain?.reportLink);
    };

    public copyReportCheckSum = checkSum => {
        this._commonUtils.copyTextToClipboard(
            checkSum,
            () => {
                this.store$.dispatch(AlertSuccess({ message: 'Copied checkSum to clipboard' }));
            },
            () => {
                this.store$.dispatch(AlertError({ message: 'Failed to copy' }));
            }
        );
    };

    closeModal = () => {
        this.dialogRef.close();
    };

    public startReportsMonitoring = (delay: boolean, timeStamp: number) => {
        this.reportCutoffTimestamp = this.reportCutoffTimestamp || timeStamp;
        const date = this.record?.updateOn || this.record?.createdOn;
        if (!timeStamp && CommonUtilsService.isOlderThanGivenTime(date, 1)) {
            this.getReports(true);
            return;
        }
        this.loadingReports = true;
        this.store$.dispatch(
            PollForEvents({
                appType: 'APP',
                resourceId: this.record?.recordId,
                resourceType: 'REPORTS',
                serviceId: this.serviceId,
                delay: delay,
                timeStamp,
                noAlerts: true,
            })
        );
    };

    private checkEvents = (logs: NotificationEvent[]) => {
        if (logs?.length === 0 || !this.loadingReports) {
            return;
        }
        this.checkReportEvents(logs);
    };

    private checkReportEvents = (logs: NotificationEvent[]) => {
        const reportsCutoffEvents = logs?.filter(
            event => !this.reportCutoffTimestamp || new Date(event.createdOn)?.getTime() > this.reportCutoffTimestamp
        );
        const reportEvent = reportsCutoffEvents
            ?.filter(
                event =>
                    this.triggeredTransformations.length === 0 ||
                    this.triggeredTransformations.indexOf(event.payload?.transformationName) > -1
            )
            .find(log =>
                [
                    'OUTBOUND_TRANSFORMATION_STARTED',
                    'OUTBOUND_TRANSFORMATION_SKIPPED',
                    'OUTBOUND_TRANSFORMATION_COMPLETED',
                    'OUTBOUND_TRANSFORMATION_TERMINATED',
                ].includes(log.eventType)
            );
        let foundTransformation = false;
        if (this.triggeredTransformations?.length > 0) {
            reportsCutoffEvents
                .filter(log =>
                    [
                        'OUTBOUND_TRANSFORMATION_STARTED',
                        'OUTBOUND_TRANSFORMATION_SKIPPED',
                        'OUTBOUND_TRANSFORMATION_COMPLETED',
                        'OUTBOUND_TRANSFORMATION_TERMINATED',
                    ].includes(log.eventType)
                )
                ?.forEach(log => {
                    const index = this.triggeredTransformations.findIndex(name => name === log?.payload.transformationName);
                    index > -1 && this.triggeredTransformations.splice(index, 1);
                    index > -1 && (foundTransformation = true);
                });
        }
        switch (reportEvent?.eventType) {
            case 'OUTBOUND_TRANSFORMATION_SKIPPED':
            case 'OUTBOUND_TRANSFORMATION_COMPLETED':
            case 'OUTBOUND_TRANSFORMATION_TERMINATED':
                foundTransformation &&
                    reportEvent?.payload?.errorStatus &&
                    this.store$.dispatch(AlertError({ message: reportEvent?.payload?.errorStatus }));
                this.getReports(true);
                if (this.triggeredTransformations?.length > 0) {
                    this.reportCutoffTimestamp = new Date(reportEvent.createdOn).getTime() + 1;
                    this.store$.dispatch(
                        PollForEvents({
                            appType: 'APP',
                            resourceId: this.record?.recordId,
                            resourceType: 'REPORTS',
                            serviceId: this.serviceId,
                            delay: true,
                            timeStamp: this.reportCutoffTimestamp,
                            noAlerts: true,
                        })
                    );
                    return;
                } else {
                    this.reportCutoffTimestamp = undefined;
                }
                this.loadingReports = false;
                break;
        }
    };

    private initiateSubscriptions() {
        this.reportsLoading = this.store$.select(getReportsLoadingStatus$(this.record?.recordId)).pipe(takeUntil(this.unSubscribe));
        this.store$
            .select(getInstanceReportsMap$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(instanceVsReports => {
                this.reports = CommonUtilsService.cloneObject(instanceVsReports?.[this.record?.recordId] || []);
            });

        this.actions$
            .pipe(
                ofType(GeneratedReport),
                takeUntil(this.unSubscribe),
                delay(10),
                filter(event => event.instanceId === this.record?.recordId)
            )
            .subscribe(event => {
                this.startedReportGeneration = false;
                this.loadingReports = true;
                this.reports
                    .find(orgMap => orgMap.id === event.repositoryId)
                    ?.chains?.find(chain => chain.name === event.chainName)
                    ?.transformations?.forEach(transformation => {
                        transformation?.reports?.forEach(report => {
                            const reportIndex = report.reportsList?.findIndex(item => item.transformationName === event.transformationName);
                            reportIndex > -1 && report.reportsList.splice(reportIndex, 1);
                        });
                    });
                if (event.transformationName) {
                    this.triggeredTransformations = [...this.triggeredTransformations, event.transformationName];
                }
                this.startReportsMonitoring(true, event.timeStamp);
            });

        this.actions$
            .pipe(
                ofType(PollingTerminated),
                takeUntil(this.unSubscribe),
                delay(10),
                filter(event => event?.resourceId === this.record?.recordId)
            )
            .subscribe(event => {
                switch (event?.resourceType) {
                    case 'REPORTS':
                        this.loadingReports = false;
                        this.triggeredTransformations = [];
                        this.getReports(true);
                        event?.timeStamp &&
                            this.store$.dispatch(
                                AlertError({
                                    message: 'Report generation is taking time, check after some time',
                                })
                            );
                        break;
                }
            });
        this.actions$
            .pipe(
                ofType(GenerateReportError),
                takeUntil(this.unSubscribe),
                delay(10),
                filter(event => event.instanceId === this.record?.recordId)
            )
            .subscribe(event => {
                this.startedReportGeneration = false;
                if (event.transformationName) {
                    this.triggeredTransformations = this.triggeredTransformations.filter(name => name !== event.transformationName);
                }
            });

        this.store$
            .select(getNotifications$(this.record?.recordId))
            .pipe(takeUntil(this.unSubscribe), delay(200))
            .subscribe(logs => {
                this.checkEvents(logs);
            });
    }

    ngOnInit(): void {
        this.initiateSubscriptions();

        if (this.record?.instanceState === 'REQUEST_CANCELLED') {
            this.isCancelledProcess = true;
        } else {
            this.isCancelledProcess = false;
        }
        this.getReports(true);
    }

    ngOnDestroy(): void {
        this.unSubscribe.next();
        this.unSubscribe.complete();
    }
}
